import { useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../hooks/redux";
import { selectIsAuthenticated } from "../../redux/Auth/auth.slice";
import { MobileTabBar } from "./MobileTabBar";
import { useMobileTopBottomColor } from "../../hooks/useMobileTopBottomColor";
import { circadiaEngineProvider } from "../../AppController";
import { ROUTES } from "../../router/mobile/routes";
import styles from "./styles.module.scss";

/**
 * Routes where the bottom tab bar is visible. Inside the install wizard
 * (Step 1–4 + Complete) the tabs are hidden so the wizard can use the full
 * viewport for its primary CTA without being clipped by the bar.
 */
const TAB_VISIBLE_ROUTES = new Set<string>([
  ROUTES.DASHBOARD,
  ROUTES.PENDING_USERS,
  ROUTES.PROFILE,
]);

export default function FieldSetupMobileLayout() {
  useMobileTopBottomColor({ circadiaEngineProvider });
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);

  // Defensive: if we land on this layout but the URL hash is something the
  // public router was responsible for (e.g. login flow), force-replace to
  // the dashboard. Without this, setup-only users can see a blank Outlet
  // for a frame after login because the route change races the auth swap.
  useEffect(() => {
    if (!isAuthenticated) return;
    const isKnown =
      pathname === ROUTES.LOGIN ||
      pathname === ROUTES.DASHBOARD ||
      pathname === ROUTES.PROFILE ||
      pathname === ROUTES.PENDING_USERS ||
      pathname.startsWith("/users/") ||
      pathname.startsWith("/setup/");
    if (!isKnown) {
      navigate(ROUTES.DASHBOARD, { replace: true });
    }
  }, [isAuthenticated, pathname, navigate]);

  const showTabs = isAuthenticated && TAB_VISIBLE_ROUTES.has(pathname);

  return (
    <div className={styles.shell}>
      <div
        className={styles.body}
        style={
          showTabs
            ? {
                paddingBottom: "calc(56px + env(safe-area-inset-bottom, 0px))",
              }
            : undefined
        }
      >
        <Outlet />
      </div>
      {showTabs && <MobileTabBar />}
    </div>
  );
}
